import { useEffect, useState } from "react";
import { QueryStatus } from "../../app/types";
import { useAppDispatch } from "../../app/hooks";
import {
  setAdminAuthority,
  setCurrentUser,
  setUserRoles
} from "../Users/usersSlice";
import { adminAuthorityFromCaller } from "./adminAccess";
import { CallerClaims, Role, rolesFromAuthUser } from "./roles";
import { User } from "../../API";
import { Mixpanel } from "../Analytics/Mixpanel";
import { fetchUsersByEmail } from "../Users/usersAPI";
import { useAllPrograms } from "../Programs/programUtils";
import { useAllNetworks } from "../Networks/networkUtils";
import { useAllInstitutions } from "../Institutions/institutionUtils";
import { useFirebaseFeatureFlags } from "../Firebase/firebaseUtils";
import { refreshMessageQueue } from "../MessageQueue/messageQueueSlice";

/**
 * `/get-user` returns a top-level `caller` field alongside the User payload
 * (added in api/get-user Commit C). The auto-generated `User` type doesn't
 * know about it, so cast locally where we read it.
 */
type UserWithCaller = User & { caller?: CallerClaims | null };

interface useAuthProps {
  /**
   * Resolved caller from the simpl-auth-authorizer Lambda — published
   * to the React tree by `<SimplAuthGuard>` once the session probe
   * settles on `authed`. The same payload shape is returned for
   * Cognito-issued and simpl_auth-issued JWTs, so this hook works
   * identically in the bridge and post-bridge worlds.
   */
  caller?: CallerClaims | null;
}

/**
 * Bootstraps Redux state for an authenticated session: identifies the
 * user in Mixpanel, resolves their User row from `/users-by-email`,
 * loads the programs / networks / institutions / feature flags the
 * shell needs, and seeds `usersSlice` with the resolved roles.
 *
 * Driven off `caller` (not Amplify) so the hook fires identically for
 * Cognito-bridge users and post-cutover simpl_auth-cookie users. The
 * caller is published by `<SimplAuthGuard>` via
 * `<SimplAuthCallerContext>`, so consumers normally read it from
 * `useSimplAuthCaller()` and pass it straight through here.
 */
export const useAuth = ({ caller }: useAuthProps) => {
  const [ddbUser, setDdbUser] = useState<User | null>(null);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  const { getAllPrograms } = useAllPrograms();
  const { getAllNetworks } = useAllNetworks();
  const { getAllInstitutions } = useAllInstitutions();
  const { getFeatureFlags } = useFirebaseFeatureFlags();

  const handleUserChange = async (caller: CallerClaims) => {
    setStatus("loading");
    setError(null);
    try {
      // `caller.roles` is authoritative — the simpl-auth-authorizer
      // Lambda has already resolved Cognito groups + simpl_auth
      // `user_grants` into the same canonical vocabulary. Seed the
      // slice immediately so any UI that renders during the User
      // lookup window sees consistent role gating; we re-dispatch
      // below after `usersAPI.fetchUsersByEmail` returns in case the
      // User row carried newer caller data than the JWT did.
      const initialRoles = rolesFromAuthUser({ caller });
      dispatch(setUserRoles(initialRoles));
      dispatch(setAdminAuthority(adminAuthorityFromCaller(caller)));

      const callerEmail = caller.email?.toLowerCase();
      if (!callerEmail) {
        setStatus("failed");
        const msg = "No email on caller — authorizer did not surface one";
        setError(msg);
        console.error(msg);
        return;
      }

      Mixpanel.identify(callerEmail);

      let usersWithEmail: User[] | null = null;
      try {
        usersWithEmail = await fetchUsersByEmail(callerEmail);
      } catch (e: any) {
        throw e;
      }

      if (!usersWithEmail || usersWithEmail.length === 0) {
        // No corresponding User row — `<DefaultHome>` shows the
        // `NoDDBUser` notification and a sign-out CTA.
        console.error("this email has no associated user in Dynamo.");
        setStatus("succeeded");
        return;
      }

      // Should only ever be one row per email. The list-shape is a
      // historical artifact of the underlying GSI query helper.
      const firstUserInList = usersWithEmail[0] as UserWithCaller;
      setDdbUser(firstUserInList);
      dispatch(setCurrentUser(firstUserInList));

      // Re-resolve roles using the User-row's `caller` payload when
      // present (it may carry newer entitlements than the JWT) and
      // fall back to the JWT caller otherwise. Also pass the User
      // row's program memberships so `Role.ProgramAdmin` is recovered
      // from simpl-api's authoritative `programMembership` rows when
      // medqip-derived `caller.roles` doesn't carry it (e.g. ETL
      // filters out the user's program — see `roles.ts` JSDoc).
      const finalRoles = rolesFromAuthUser({
        caller: firstUserInList.caller ?? caller,
        programMemberships: firstUserInList.programMemberships?.items
      });
      dispatch(setUserRoles(finalRoles));

      const isSysAdmin = finalRoles.includes(Role.SystemAdmin);

      const loadPromises = [
        getAllPrograms(),
        getAllNetworks(),
        ...(isSysAdmin ? [getAllInstitutions()] : [])
      ];
      await Promise.all(loadPromises);

      await getFeatureFlags();
      dispatch(refreshMessageQueue());

      setStatus("succeeded");
    } catch (error) {
      setStatus("failed");
      const errorString = `Error in useAuth: ${JSON.stringify(error)}`;
      setError(errorString);
      console.error(errorString);
    }
  };

  useEffect(() => {
    if (caller) {
      handleUserChange(caller);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caller?.email]);

  return {
    ddbUser,
    status,
    error
  };
};

export const AuthNotifications = {
  NoDDBUser:
    "Your account does not have a corresponding User. Please try refreshing your browser or contact your administrator for further assistance.",
  FetchUserError:
    "There was an error fetching this user. Please try again or contact your administrator."
};
